import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import PendingRequestsTable from '@/tables/PendingRequestsTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/pending-requests/',
)({
  component: PendingRequestsScreen,
});

function PendingRequestsScreen() {
  const { t } = useTranslation();

  const { groupName } = Route.useParams();
  const { setCreatedRequestId, setCanceledRequestId } = useFollowUpsAlert();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  // Create and cancel both land here, so this screen is where their alerts are
  // read and cleared: legacy's notices live for one page load.
  //
  // The clear is DEFERRED and CANCELLABLE, not inline, because an inline
  // cleanup here is a real bug under React 18 StrictMode (`main.tsx:12`):
  // StrictMode double-invokes a fresh mount's effects in dev -- setup,
  // cleanup, setup again, all synchronously in the same commit -- to catch
  // exactly this shape of bug. An inline `setCreatedRequestId(null)` in the
  // cleanup fired on that PHANTOM unmount too, wiping the banner
  // `handleSubmit` had just set and navigated here to show, before it ever
  // painted -- reproduced as `getByRole('alert')` never resolving and the
  // test hanging to its own timeout, on a row that psql confirms WAS created.
  // Scheduling the clear past the current synchronous execution, and
  // cancelling it from the very next mount's effect run, is what lets a
  // GENUINE unmount (nothing left to cancel it) still clear the banner while
  // the StrictMode artifact (cancelled a tick later by the second, real
  // setup) does not.
  const pendingClearRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => {
    if (pendingClearRef.current !== undefined) {
      clearTimeout(pendingClearRef.current);
      pendingClearRef.current = undefined;
    }
    return () => {
      pendingClearRef.current = setTimeout(() => {
        setCreatedRequestId(null);
        setCanceledRequestId(null);
      }, 0);
    };
  }, [setCreatedRequestId, setCanceledRequestId]);

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.pending-requests')}
          helpKey="pending requests"
        />
      </GroupHeaderWrapper>
      <PendingRequestsTable
        groupId={groupData?.id ?? 0}
        groupName={groupData?.name ?? ''}
        groupSlug={groupName}
      />
    </>
  );
}
